import type { ReactNode } from 'react'
import { Icon } from '../ui/Icon'

/** What every page of More's stage carries, whichever page it is. */
export type StageChrome = {
  /** The way back: from an item to its list, from a list to wherever the stage was. */
  onBack: () => void
  /** Whether the side panel is showing — the toggle draws its own state. */
  railOpen: boolean
  /** Show/hide the side panel, which is what grows or shrinks this page. */
  onToggleRail: () => void
}

/**
 * ONE PAGE OF MORE, ON THE STAGE — owner, 2026-10-22: 在寬螢幕的狀態下, 點擊 Artifact /
 * Chrome tabs / Backups 按鈕會在右側主顯示欄位 (session) 裡開啟並且列表所有的 … 點擊任何一個
 * 項目, 則會在右側主顯示匡裡面顯示 … 的內容 (非彈跳視窗), 顯示匡的左上角有返回頁面的按鈕 …
 * 如果是在窄螢幕的狀態, 則是使用蓋板的方式來顯示.
 *
 * A list and an item are both this: Back leading the header, the title where the artifact
 * viewer puts its title, the page below. The header is the artifact viewer's own (.av-hd),
 * so an artifact opened from the list and the list behind it read as one place.
 *
 * Where it sits — beside the rail or over the whole screen — is #moreStage's (MoreStage),
 * decided by the same width that docks the rail.
 */
export function StagePage({
  title,
  sub,
  glyph,
  backLabel,
  onBack,
  railOpen,
  onToggleRail,
  tools,
  footer,
  className,
  children,
}: StageChrome & {
  title: string
  /** The line under the title. */
  sub?: ReactNode
  /** A face or chip before the title. */
  glyph?: ReactNode
  /** What Back goes back to, said: "Back to Chrome tabs". */
  backLabel: string
  /** Controls at the header's end. */
  tools?: ReactNode
  /** A part that stays at the bottom while the page scrolls — a box to type in. */
  footer?: ReactNode
  className?: string
  children: ReactNode
}) {
  return (
    <section className={`stage-page${className ? ` ${className}` : ''}`} aria-label={title}>
      <header className="av-hd sp-hd">
        <button type="button" className="av-icon sp-back" onClick={onBack} aria-label={backLabel} title={backLabel}>
          <Icon name="chevron_left" size={22} />
        </button>
        {/* The side panel's toggle, as in the artifact viewer — wide only (shell.css). */}
        <button
          type="button"
          className={`av-icon av-rail${railOpen ? ' on' : ''}`}
          onClick={onToggleRail}
          aria-label={railOpen ? 'Hide the side panel' : 'Show the side panel'}
          aria-pressed={railOpen}
          title={railOpen ? 'Hide the side panel' : 'Show the side panel'}
        >
          <Icon name="menu" size={18} label="Side panel" />
        </button>
        {glyph}
        <div className="av-title">
          <h2 className="av-name sp-name" title={title}>
            {title}
          </h2>
          {sub && <span className="av-sub">{sub}</span>}
        </div>
        {tools}
      </header>
      <div className="sp-body">
        <div className="sp-col">{children}</div>
      </div>
      {footer && (
        <div className="sp-foot">
          <div className="sp-col">{footer}</div>
        </div>
      )}
    </section>
  )
}
